import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * PLP size picker – selects a variant without navigating to the product page.
 *
 * @extends {Component<{}>}
 */
class ProductSizesComponent extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('.product-sizes--item');
    if (!button || !this.contains(button)) return;

    event.preventDefault();
    event.stopPropagation();

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    this.#selectVariant(/** @type {HTMLButtonElement} */ (button), variantId);
  };

  /**
   * @param {HTMLButtonElement} button
   * @param {string} variantId
   */
  #selectVariant(button, variantId) {
    this.querySelectorAll('.product-sizes--item').forEach((item) => {
      const isSelected = item === button;
      item.classList.toggle('product-sizes--item-selected', isSelected);
      item.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    const optionValueId = button.dataset.optionValueId ?? '';
    this.dispatchEvent(new VariantSelectedEvent({ id: optionValueId }));

    const productUrl = this.dataset.productUrl?.split('?')[0];
    if (!productUrl) return;

    const requestUrl = `${productUrl}?variant=${variantId}&section_id=section-rendering-product-card`;
    this.#fetchUpdatedSection(requestUrl);
  }

  /** @param {string} requestUrl */
  #fetchUpdatedSection(requestUrl) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const textContent =
          html.querySelector('product-sizes-component script[type="application/json"]')?.textContent ??
          html.querySelector('variant-picker script[type="application/json"]')?.textContent;

        if (!textContent) return;

        const newSizes = html.querySelector('product-sizes-component');
        if (newSizes) {
          morph(this, newSizes);
        }

        this.dispatchEvent(
          new VariantUpdateEvent(JSON.parse(textContent), this.id, {
            html,
            productId: this.dataset.productId ?? '',
          })
        );
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error(error);
      });
  }
}

if (!customElements.get('product-sizes-component')) {
  customElements.define('product-sizes-component', ProductSizesComponent);
}
